type SliderInputDomObject = {
  input: HTMLElement;
  range: HTMLElement;
  slider: HTMLElement;
  handle: HTMLElement;
  line: HTMLElement;
  lineFill?: HTMLElement;
  title: HTMLElement;
  hint: HTMLElement;
  hintPopup: HTMLElement;
}

type SliderInputDataAttributesObject = {
  min: number;
  max: number;
  step: number;
  value: number;
}

type sliderInputSettingsObject = {
  onTitleInputChange?: (value: number) => void;
  onRangeInputChange?: (value: number) => void;
}

export default class SliderInput {
  private sliderObject: any;
  private dataAttributesObject: any;
  private domObject: any;
  private settings: sliderInputSettingsObject;
  private currentValue: number;
  private isDragging: boolean = false;
  private stepDecimals: number = 0;

  constructor(input: HTMLElement, settings: sliderInputSettingsObject = { onTitleInputChange: () => {}, onRangeInputChange: () => {} }) {
    const defaultSettings = {
      onTitleInputChange: () => {},
      onRangeInputChange: () => {},
    }
    this.settings = { ...defaultSettings, ...settings };
    this.domObject = this.returnDomObject(input);
    this.dataAttributesObject = this.returnDataAtrributesObject(this.domObject.input);
    if(this.domObject.input.classList.contains('slider-input--dotted')) {
      this.createDotsOnLine();
    } 
    this.currentValue = this.normalizeValue(this.dataAttributesObject.value);
    this.stepDecimals = this.getStepDecimals(this.dataAttributesObject.step);
    this.applyRangeAttributes();
    this.setDomObjectValues();
    this.bindEvents();
  }

  returnDomObject(input: HTMLElement) {
    return {
      input: input,
      range: input.querySelector('.slider-input__input'),
      slider: input.querySelector('.slider-input__slider'),
      handle: input.querySelector('.slider-input__slider-handle'),
      line: input.querySelector('.slider-input__slider-line'),
      lineFill: input.querySelector('.slider-input__slider-line-fill'),
      titleText: input.querySelector('.slider-input__title-text'),
      titleValue: input.querySelector('.slider-input__title-value'),
      hint: input.querySelector('.slider-input__hint'),
      hintPopup: input.querySelector('.slider-input__hint-popup'),
    };
  }

  returnDataAtrributesObject(input: HTMLElement) {
    const min = this.parseNumber((input as HTMLElement).dataset.min, 0);
    const max = this.parseNumber((input as HTMLElement).dataset.max, 100);
    const step = this.parseNumber((input as HTMLElement).dataset.step, 1);
    const initial = this.parseNumber((input as HTMLElement).dataset.value, min);
    return {
      min,
      max,
      step,
      value: this.clamp(initial, min, max),
    }
  }

  setDomObjectValues() {
    const percent = this.valueToPercent(this.currentValue);
    if (this.domObject.titleValue) {
      this.domObject.titleValue.value = String(this.currentValue);
      this.settings.onTitleInputChange?.(this.currentValue);
    }
    if (this.domObject.range) {
      (this.domObject.range as HTMLInputElement).value = String(this.currentValue);
      this.settings.onRangeInputChange?.(this.currentValue);
    }
    if (this.domObject.handle) {
      (this.domObject.handle as HTMLElement).style.left = `${percent}%`;
    }
    if (this.domObject.lineFill) {
      (this.domObject.lineFill as HTMLElement).style.width = `${percent}%`;
    } else if (this.domObject.line) {
      // fallback if no fill element present
      (this.domObject.line as HTMLElement).style.width = `${percent}%`;
    }
  }

  private bindEvents() {
    // Sync from native range input (keyboard, programmatic, or if made visible)
    if (this.domObject.range) {
      this.domObject.range.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement;
        const next = this.normalizeValue(this.parseNumber(target.value, this.currentValue));
        this.setValue(next);
      });
    }

    // Sync from title value input (manual number entry)
    if (this.domObject.titleValue) {
      const titleInput = this.domObject.titleValue as HTMLInputElement;
      titleInput.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement;
        const nextRaw = this.parseNumber(target.value, this.currentValue);
        const next = this.normalizeValue(nextRaw);
        this.setValue(next);
        // Ensure input reflects clamped/stepped value
        target.value = String(this.currentValue);
      });
    }

    // Pointer interactions on the custom slider
    if (this.domObject.slider) {
      const onPointerDown = (e: PointerEvent) => {
        this.isDragging = true;
        (this.domObject.slider as HTMLElement).setPointerCapture?.(e.pointerId);
        this.updateFromPointer(e);
      };
      const onPointerMove = (e: PointerEvent) => {
        if (!this.isDragging) return;
        this.updateFromPointer(e);
      };
      const onPointerUp = (e: PointerEvent) => {
        this.isDragging = false;
        (this.domObject.slider as HTMLElement).releasePointerCapture?.(e.pointerId);
      };

      this.domObject.slider.addEventListener('pointerdown', onPointerDown);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    }
  }

  private updateFromPointer(e: PointerEvent) {
    const sliderEl = this.domObject.slider as HTMLElement;
    const rect = sliderEl.getBoundingClientRect();
    const relative = this.clamp(e.clientX - rect.left, 0, rect.width);
    const ratio = rect.width === 0 ? 0 : relative / rect.width;
    const rawValue = this.percentToValue(ratio * 100);
    this.setValue(rawValue);
  }

  private setValue(next: number) {
    const normalized = this.normalizeValue(next);
    if (normalized === this.currentValue) return;
    this.currentValue = normalized;
    this.setDomObjectValues();
  }

  private applyRangeAttributes() {
    const range = this.domObject.range as HTMLInputElement | null;
    if (!range) return;
    range.min = String(this.dataAttributesObject.min);
    range.max = String(this.dataAttributesObject.max);
    range.step = String(this.dataAttributesObject.step);
    range.value = String(this.currentValue);
  }

  private valueToPercent(value: number): number {
    const { min, max } = this.dataAttributesObject;
    if (max === min) return 0;
    return ((value - min) / (max - min)) * 100;
  }

  private percentToValue(percent: number): number {
    const { min, max } = this.dataAttributesObject;
    const ratio = this.clamp(percent, 0, 100) / 100;
    return min + ratio * (max - min);
  }

  private normalizeValue(raw: number): number {
    const { min, max, step } = this.dataAttributesObject;
    const stepped = min + Math.round((raw - min) / step) * step;
    const clamped = this.clamp(stepped, min, max);
    return Number(clamped.toFixed(this.stepDecimals));
  }

  private getStepDecimals(step: number): number {
    const text = String(step);
    const dot = text.indexOf('.')
    return dot === -1 ? 0 : text.length - dot - 1;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private parseNumber(value: string | number | undefined, fallback: number): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : fallback;
    }
    return fallback;
  }

  private createDotsOnLine() {
    const line = this.domObject.line as HTMLElement;
    const numberOfDots = this.dataAttributesObject.max - this.dataAttributesObject.min;
    for (let i = 0; i < numberOfDots; i++) {
      const dot = document.createElement('div');
      dot.classList.add('slider-input__slider-line-dot');
      dot.style.left = `${i * 100 / numberOfDots}%`;
      line.appendChild(dot);
    }
  }
}
